import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./generalreport.css";

import {
  AppsOutlined,
  AssignmentOutlined,
  CloseOutlined,
  DashboardOutlined,
  EmailOutlined,
  FilterAltOutlined,
  GroupsOutlined,
  KeyboardArrowDownOutlined,
  LocalPhoneOutlined,
  LocationOnOutlined,
  RefreshOutlined,
  SearchOutlined,
  TableRowsOutlined,
  TuneOutlined,
  VisibilityOutlined,
  WorkOutlineOutlined,
  SchoolOutlined,
  CheckCircleOutlined,
  PaymentsOutlined,
  ManageSearchOutlined,
  CalendarMonthOutlined,
  ArrowForwardOutlined,
} from "@mui/icons-material";

const API = process.env.REACT_APP_SERVER_SCRIPT_HOST;

const getName = (candidate) =>
  [
    candidate?.first_Name,
    candidate?.sur_Name,
    candidate?.other_Name,
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim() || "Unnamed Applicant";

const getInitials = (candidate) => {
  const name = getName(candidate);

  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
};

const getProgramme = (candidate) =>
  candidate?.programme_of_study || "Programme not specified";

const getCourse = (candidate) =>
  candidate?.course_of_study || "Course not specified";

const getApplication = (candidate) =>
  candidate?.application_number || "N/A";

const getPaymentStatus = (candidate) =>
  candidate?.account_status?.paymentStatus || "Pending";

const getAdmissionStatus = (candidate) =>
  candidate?.admissions?.status || "Pending";

const getAcceptanceStatus = (candidate) =>
  candidate?.admissions?.acceptance_of_adm || "Pending";

const getDate = (candidate) =>
  candidate?.registered_date?.date || "—";

const getPhone = (candidate) =>
  candidate?.phone_number
    ? `+234${String(candidate.phone_number).replace(/^0+/, "")}`
    : "Not provided";

const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");

const isNursing = (candidate) => {
  const programme = normalize(candidate?.programme_of_study);
  const course = normalize(candidate?.course_of_study);

  return (
    programme.includes("nursing") ||
    course.includes("nursing") ||
    course.includes("general-nursing")
  );
};

const isMidwifery = (candidate) => {
  const programme = normalize(candidate?.programme_of_study);
  const course = normalize(candidate?.course_of_study);

  return (
    programme.includes("midwifery") ||
    course.includes("midwifery")
  );
};

function StatusPill({ value, type = "default" }) {
  const normalized = normalize(value);

  let label = value || "Unknown";

  if (normalized === "success") label = "Successful";
  if (normalized === "completed") label = "Completed";
  if (normalized === "accepted") label = "Accepted";
  if (normalized === "admitted") label = "Admitted";
  if (normalized === "recommended") label = "Recommended";
  if (normalized === "pending") label = "Pending";
  if (normalized === "failed") label = "Failed";
  if (normalized === "rejected") label = "Rejected";

  return (
    <span
      className={`report-status report-status--${normalized} report-status--${type}`}
    >
      <span className="report-status__dot" />
      {label}
    </span>
  );
}

function ApplicantAvatar({ candidate, large = false }) {
  return (
    <div
      className={`report-avatar ${
        large ? "report-avatar--large" : ""
      }`}
    >
      {candidate?.passport?.url ? (
        <img
          src={candidate.passport.url}
          alt={getName(candidate)}
        />
      ) : (
        <span>{getInitials(candidate)}</span>
      )}
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
  description,
  accent,
  active,
  onClick,
}) {
  return (
    <button
      type="button"
      className={`report-metric ${
        active ? "is-active" : ""
      } report-metric--${accent}`}
      onClick={onClick}
    >
      <div className="report-metric__top">
        <div className="report-metric__icon">
          {icon}
        </div>

        <span className="report-metric__index">
          {accent === "blue"
            ? "01"
            : accent === "violet"
            ? "02"
            : accent === "gold"
            ? "03"
            : "04"}
        </span>
      </div>

      <div className="report-metric__value">
        {value ?? 0}
      </div>

      <div className="report-metric__label">
        {label}
      </div>

      <div className="report-metric__description">
        {description}
      </div>

      <div className="report-metric__arrow">
        <ArrowForwardOutlined />
      </div>
    </button>
  );
}

function ApplicantModal({ candidate, onClose }) {
  useEffect(() => {
    if (!candidate) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [candidate, onClose]);

  if (!candidate) return null;

  return (
    <div
      className="report-modal-backdrop"
      onMouseDown={onClose}
    >
      <aside
        className="report-modal"
        onMouseDown={(event) =>
          event.stopPropagation()
        }
      >
        <div className="report-modal__topline" />

        <header className="report-modal__header">
          <div className="report-modal__identity">
            <ApplicantAvatar
              candidate={candidate}
              large
            />

            <div>
              <span className="report-modal__eyebrow">
                APPLICANT PROFILE
              </span>

              <h2>{getName(candidate)}</h2>

              <div className="report-modal__application">
                <span>
                  {getApplication(candidate)}
                </span>

                <StatusPill
                  value={getAdmissionStatus(candidate)}
                />
              </div>
            </div>
          </div>

          <button
            type="button"
            className="report-modal__close"
            onClick={onClose}
            aria-label="Close profile"
          >
            <CloseOutlined />
          </button>
        </header>

        <div className="report-modal__body">
          <section className="modal-overview">
            <div className="modal-overview__main">
              <span>ACADEMIC PLACEMENT</span>

              <h3>{getProgramme(candidate)}</h3>

              <strong>{getCourse(candidate)}</strong>
            </div>

            <div className="modal-overview__side">
              <span>LEVEL</span>
              <strong>
                {candidate?.level || "—"}
              </strong>
            </div>
          </section>

          <section className="modal-section">
            <div className="modal-section__heading">
              <span>IDENTITY & CONTACT</span>
              <h3>Applicant information</h3>
            </div>

            <div className="modal-info-grid">
              <div className="modal-info">
                <div className="modal-info__icon">
                  <EmailOutlined />
                </div>

                <div>
                  <span>Email address</span>
                  <strong>
                    {candidate?.email ||
                      "Not provided"}
                  </strong>
                </div>
              </div>

              <div className="modal-info">
                <div className="modal-info__icon">
                  <LocalPhoneOutlined />
                </div>

                <div>
                  <span>Phone number</span>
                  <strong>
                    {getPhone(candidate)}
                  </strong>
                </div>
              </div>

              <div className="modal-info">
                <div className="modal-info__icon">
                  <LocationOnOutlined />
                </div>

                <div>
                  <span>State of origin</span>
                  <strong>
                    {candidate?.State_of_origin ||
                      "Not provided"}
                  </strong>
                </div>
              </div>

              <div className="modal-info">
                <div className="modal-info__icon">
                  <WorkOutlineOutlined />
                </div>

                <div>
                  <span>Local government</span>
                  <strong>
                    {candidate?.Local_Govt_Area ||
                      "Not provided"}
                  </strong>
                </div>
              </div>
            </div>
          </section>

          <section className="modal-section">
            <div className="modal-section__heading">
              <span>APPLICATION JOURNEY</span>
              <h3>Current progression</h3>
            </div>

            <div className="modal-timeline">
              <div className="modal-timeline__item completed">
                <span />
                <div>
                  <strong>
                    Application registered
                  </strong>

                  <small>
                    {getDate(candidate)}
                  </small>
                </div>
              </div>

              <div className="modal-timeline__item">
                <span />
                <div>
                  <strong>
                    Admission status
                  </strong>

                  <small>
                    {getAdmissionStatus(candidate)}
                  </small>
                </div>
              </div>

              <div className="modal-timeline__item">
                <span />
                <div>
                  <strong>
                    Acceptance response
                  </strong>

                  <small>
                    {getAcceptanceStatus(candidate)}
                  </small>
                </div>
              </div>
            </div>
          </section>

          <section className="modal-section">
            <div className="modal-section__heading">
              <span>FINANCIAL RECORD</span>
              <h3>Payment overview</h3>
            </div>

            <div className="modal-finance-grid">
              <div>
                <span>Payment</span>
                <StatusPill
                  value={getPaymentStatus(candidate)}
                  type="payment"
                />
              </div>

              <div>
                <span>Reference</span>
                <strong>
                  {candidate?.account_status
                    ?.reference_number ||
                    "Not assigned"}
                </strong>
              </div>

              <div>
                <span>Transaction</span>
                <strong>
                  {candidate?.account_status
                    ?.transaction_id ||
                    "Not assigned"}
                </strong>
              </div>
            </div>
          </section>

          <section className="modal-section">
            <div className="modal-section__heading">
              <span>APPLICATION METADATA</span>
              <h3>Record details</h3>
            </div>

            <div className="modal-record-grid">
              <div>
                <span>Application number</span>
                <strong>
                  {getApplication(candidate)}
                </strong>
              </div>

              <div>
                <span>Gender</span>
                <strong>
                  {candidate?.gender || "—"}
                </strong>
              </div>

              <div>
                <span>Registration status</span>
                <strong>
                  {candidate?.account_status
                    ?.registration_status ||
                    "—"}
                </strong>
              </div>

              <div>
                <span>Nationality</span>
                <strong>
                  {candidate?.Nationality ||
                    "—"}
                </strong>
              </div>
            </div>
          </section>
        </div>

        <footer className="report-modal__footer">
          <span>
            Record ID · {getApplication(candidate)}
          </span>

          <button
            type="button"
            onClick={onClose}
          >
            Close profile
          </button>
        </footer>
      </aside>
    </div>
  );
}

function TableView({ applicants, onView }) {
  return (
    <section className="report-data-panel">
      <div className="report-data-panel__header">
        <div>
          <span>DATA EXPLORER</span>
          <h2>Applicant directory</h2>
          <p>
            Detailed applicant records within the
            active reporting dataset.
          </p>
        </div>

        <div className="report-data-count">
          <strong>{applicants.length}</strong>
          <span>records</span>
        </div>
      </div>

      <div className="report-table-scroll">
        <table className="report-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Applicant</th>
              <th>Application</th>
              <th>Programme</th>
              <th>Gender</th>
              <th>Contact</th>
              <th>Payment</th>
              <th>Admission</th>
              <th>View</th>
            </tr>
          </thead>

          <tbody>
            {applicants.map((candidate, index) => (
              <tr
                key={
                  candidate?._id ||
                  candidate?.application_number ||
                  index
                }
              >
                <td>
                  <span className="table-index">
                    {String(index + 1).padStart(
                      2,
                      "0"
                    )}
                  </span>
                </td>

                <td>
                  <div className="table-applicant">
                    <ApplicantAvatar
                      candidate={candidate}
                    />

                    <div>
                      <strong>
                        {getName(candidate)}
                      </strong>

                      <span>
                        {candidate?.State_of_origin ||
                          "Location unavailable"}
                      </span>
                    </div>
                  </div>
                </td>

                <td>
                  <span className="application-code">
                    {getApplication(candidate)}
                  </span>
                </td>

                <td>
                  <div className="programme-stack">
                    <strong>
                      {getProgramme(candidate)}
                    </strong>

                    <span>
                      {getCourse(candidate)}
                    </span>
                  </div>
                </td>

                <td>
                  <span className="table-muted">
                    {candidate?.gender || "—"}
                  </span>
                </td>

                <td>
                  <div className="table-contact">
                    <span>
                      {getPhone(candidate)}
                    </span>

                    <small>
                      {candidate?.email || "No email"}
                    </small>
                  </div>
                </td>

                <td>
                  <StatusPill
                    value={getPaymentStatus(
                      candidate
                    )}
                    type="payment"
                  />
                </td>

                <td>
                  <StatusPill
                    value={getAdmissionStatus(
                      candidate
                    )}
                  />
                </td>

                <td>
                  <button
                    type="button"
                    className="table-view-button"
                    onClick={() =>
                      onView(candidate)
                    }
                    aria-label={`View ${getName(
                      candidate
                    )}`}
                  >
                    <VisibilityOutlined />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CardView({ applicants, onView }) {
  return (
    <section className="report-card-grid">
      {applicants.map((candidate, index) => (
        <article
          className="report-applicant-card"
          key={
            candidate?._id ||
            candidate?.application_number ||
            index
          }
        >
          <div className="report-card__number">
            {String(index + 1).padStart(2, "0")}
          </div>

          <div className="report-card__top">
            <div className="report-card__identity">
              <ApplicantAvatar
                candidate={candidate}
                large
              />

              <div>
                <h3>{getName(candidate)}</h3>

                <span>
                  {getApplication(candidate)}
                </span>
              </div>
            </div>

            <StatusPill
              value={getAdmissionStatus(candidate)}
            />
          </div>

          <div className="report-card__programme">
            <span>ACADEMIC PLACEMENT</span>

            <strong>
              {getProgramme(candidate)}
            </strong>

            <small>
              {getCourse(candidate)}
            </small>
          </div>

          <div className="report-card__matrix">
            <div>
              <span>Gender</span>
              <strong>
                {candidate?.gender || "—"}
              </strong>
            </div>

            <div>
              <span>Level</span>
              <strong>
                {candidate?.level || "—"}
              </strong>
            </div>

            <div>
              <span>Payment</span>
              <strong>
                {getPaymentStatus(candidate)}
              </strong>
            </div>

            <div>
              <span>Acceptance</span>
              <strong>
                {getAcceptanceStatus(candidate)}
              </strong>
            </div>
          </div>

          <div className="report-card__contact">
            <div>
              <EmailOutlined />
              <span>
                {candidate?.email || "No email"}
              </span>
            </div>

            <div>
              <LocalPhoneOutlined />
              <span>{getPhone(candidate)}</span>
            </div>
          </div>

          <footer className="report-card__footer">
            <span>{getDate(candidate)}</span>

            <button
              type="button"
              onClick={() => onView(candidate)}
            >
              View record
              <VisibilityOutlined />
            </button>
          </footer>
        </article>
      ))}
    </section>
  );
}

function EmptyState({ clearFilters }) {
  return (
    <section className="report-empty">
      <div className="report-empty__icon">
        <ManageSearchOutlined />
      </div>

      <span className="report-empty__eyebrow">
        NO MATCHES
      </span>

      <h3>No applicants match this view</h3>

      <p>
        Adjust your search or filter configuration
        to discover additional applicant records.
      </p>

      <button
        type="button"
        onClick={clearFilters}
      >
        Reset filters
      </button>
    </section>
  );
}

export default function GeneralReport() {
  const [applicants, setApplicants] = useState([]);
  const [countS, setCountS] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [activeDataset, setActiveDataset] =
    useState("all");

  const [changeview, setChangeView] =
    useState("table");

  const [search, setSearch] = useState("");

  const [programmeFilter, setProgrammeFilter] =
    useState("all");

  const [courseFilter, setCourseFilter] =
    useState("all");

  const [genderFilter, setGenderFilter] =
    useState("all");

  const [paymentFilter, setPaymentFilter] =
    useState("all");

  const [admissionFilter, setAdmissionFilter] =
    useState("all");

  const [filtersOpen, setFiltersOpen] =
    useState(false);

  const [selectedApplicant, setSelectedApplicant] =
    useState(null);

  async function fetchReport(
    endpoint = "/general_reports",
    dataset = "all"
  ) {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(
        `${API}${endpoint}`
      );

      const result = response?.data || {};
      const records = result?.response || [];

      setApplicants(records);
      setCountS(result);
      setActiveDataset(dataset);
    } catch (err) {
      console.error(err);

      setError(
        "Unable to load the applicant report."
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchReport();
  }, []);

  /*
   * IMPORTANT:
   * Nursing and Midwifery are calculated from the
   * currently loaded applicant array.
   *
   * No separate API request is made for either.
   */
  const nursingApplicants = useMemo(
    () => applicants.filter(isNursing),
    [applicants]
  );

  const midwiferyApplicants = useMemo(
    () => applicants.filter(isMidwifery),
    [applicants]
  );

  const programmeOptions = useMemo(() => {
    return [
      ...new Set(
        applicants
          .map(
            (item) => item?.programme_of_study
          )
          .filter(Boolean)
      ),
    ].sort();
  }, [applicants]);

  const courseOptions = useMemo(() => {
    return [
      ...new Set(
        applicants
          .map(
            (item) => item?.course_of_study
          )
          .filter(Boolean)
      ),
    ].sort();
  }, [applicants]);

  const filteredApplicants = useMemo(() => {
    const query = search
      .trim()
      .toLowerCase();

    return applicants.filter((candidate) => {
      const searchable = [
        getName(candidate),
        candidate?.application_number,
        candidate?.email,
        candidate?.phone_number,
        candidate?.programme_of_study,
        candidate?.course_of_study,
        candidate?.State_of_origin,
        candidate?.Local_Govt_Area,
        candidate?.account_status
          ?.reference_number,
        candidate?.account_status
          ?.transaction_id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch =
        !query ||
        searchable.includes(query);

      const matchesProgramme =
        programmeFilter === "all" ||
        candidate?.programme_of_study ===
          programmeFilter;

      const matchesCourse =
        courseFilter === "all" ||
        candidate?.course_of_study ===
          courseFilter;

      const matchesGender =
        genderFilter === "all" ||
        normalize(candidate?.gender) ===
          normalize(genderFilter);

      const matchesPayment =
        paymentFilter === "all" ||
        normalize(
          candidate?.account_status
            ?.paymentStatus
        ) === normalize(paymentFilter);

      const matchesAdmission =
        admissionFilter === "all" ||
        normalize(
          candidate?.admissions?.status
        ) === normalize(admissionFilter);

      return (
        matchesSearch &&
        matchesProgramme &&
        matchesCourse &&
        matchesGender &&
        matchesPayment &&
        matchesAdmission
      );
    });
  }, [
    applicants,
    search,
    programmeFilter,
    courseFilter,
    genderFilter,
    paymentFilter,
    admissionFilter,
  ]);

  const activeFilterCount = [
    programmeFilter !== "all",
    courseFilter !== "all",
    genderFilter !== "all",
    paymentFilter !== "all",
    admissionFilter !== "all",
  ].filter(Boolean).length;

  const hasAnyFilter =
    Boolean(search.trim()) ||
    activeFilterCount > 0;

  function resetFilters() {
    setSearch("");
    setProgrammeFilter("all");
    setCourseFilter("all");
    setGenderFilter("all");
    setPaymentFilter("all");
    setAdmissionFilter("all");
  }

  function applyProgrammeFilter(
    programme
  ) {
    setCourseFilter("all");
    setProgrammeFilter(programme);
    setFiltersOpen(true);
  }

  function selectDataset(dataset) {
    if (dataset === "all") {
      fetchReport(
        "/general_reports",
        "all"
      );
    }

    if (dataset === "completed") {
      fetchReport(
        "/general_reports/completed",
        "completed"
      );
    }

    if (dataset === "recommended") {
      fetchReport(
        "/general_reports/recommended",
        "recommended"
      );
    }
  }

  const datasetLabel =
    activeDataset === "all"
      ? "All applicants"
      : activeDataset === "completed"
      ? "Completed applicants"
      : "Recommended applicants";

  return (
    <main className="general-report">
      <div className="general-report__shell">
        {/* COMMAND HEADER */}
        <header className="report-commandbar">
          <div className="report-commandbar__brand">
            <div className="report-commandbar__logo">
              <DashboardOutlined />
            </div>

            <div>
              <span>INTELLIGENCE / REPORTING</span>
              <strong>Admissions Observatory</strong>
            </div>
          </div>

          <div className="report-commandbar__actions">
            <div className="report-live-indicator">
              <span />
              Dataset online
            </div>

            <button
              type="button"
              onClick={() =>
                fetchReport(
                  activeDataset ===
                    "completed"
                    ? "/general_reports/completed"
                    : activeDataset ===
                      "recommended"
                    ? "/general_reports/recommended"
                    : "/general_reports",
                  activeDataset
                )
              }
              disabled={loading}
              aria-label="Refresh report"
            >
              <RefreshOutlined
                className={
                  loading
                    ? "report-refresh-spin"
                    : ""
                }
              />
            </button>
          </div>
        </header>

        {/* HERO */}
        <section className="report-hero">
          <div className="report-hero__content">
            <div className="report-hero__eyebrow">
              <span />
              APPLICATION INTELLIGENCE
            </div>

            <h1>
              See the entire
              <br />
              applicant landscape.
            </h1>

            <p>
              A centralized reporting environment
              for understanding applicant volume,
              programme distribution, admission
              progression and operational status.
            </p>

            <div className="report-hero__meta">
              <div>
                <GroupsOutlined />
                <span>
                  {applicants.length} loaded
                  records
                </span>
              </div>

              <div>
                <CalendarMonthOutlined />
                <span>{datasetLabel}</span>
              </div>
            </div>
          </div>

          <div className="report-hero__visual">
            <div className="hero-grid" />

            <div className="hero-ring hero-ring--one" />
            <div className="hero-ring hero-ring--two" />
            <div className="hero-ring hero-ring--three" />

            <div className="hero-core">
              <DashboardOutlined />

              <strong>
                {applicants.length}
              </strong>

              <span>RECORDS</span>
            </div>

            <div className="hero-data-card hero-data-card--one">
              <span>Nursing</span>
              <strong>
                {nursingApplicants.length}
              </strong>
            </div>

            <div className="hero-data-card hero-data-card--two">
              <span>Midwifery</span>
              <strong>
                {midwiferyApplicants.length}
              </strong>
            </div>
          </div>
        </section>

        {/* DATASET SWITCHER */}
        <section className="report-dataset">
          <div className="dataset-tabs">
            <button
              type="button"
              className={
                activeDataset === "all"
                  ? "active"
                  : ""
              }
              onClick={() =>
                selectDataset("all")
              }
            >
              <span>01</span>
              <div>
                <strong>All Applicants</strong>
                <small>
                  Complete applicant universe
                </small>
              </div>
            </button>

            <button
              type="button"
              className={
                activeDataset === "completed"
                  ? "active"
                  : ""
              }
              onClick={() =>
                selectDataset("completed")
              }
            >
              <span>02</span>
              <div>
                <strong>Completed</strong>
                <small>
                  Completed registrations
                </small>
              </div>
            </button>

            <button
              type="button"
              className={
                activeDataset ===
                "recommended"
                  ? "active"
                  : ""
              }
              onClick={() =>
                selectDataset("recommended")
              }
            >
              <span>03</span>
              <div>
                <strong>Recommended</strong>
                <small>
                  Recommended applicants
                </small>
              </div>
            </button>
          </div>

          <div className="dataset-status">
            <span />
            Synchronized
          </div>
        </section>

        {/* METRICS */}
        <section className="report-metrics">
          <MetricCard
            icon={<GroupsOutlined />}
            value={applicants.length}
            label="Total applicants"
            description="Current reporting population"
            accent="blue"
            active={
              activeFilterCount === 0 &&
              !search
            }
            onClick={() => {
              resetFilters();
            }}
          />

          <MetricCard
            icon={<SchoolOutlined />}
            value={nursingApplicants.length}
            label="Nursing"
            description="Derived from loaded applicant records"
            accent="violet"
            active={
              programmeFilter === "Nursing" ||
              courseFilter === "General Nursing"
            }
            onClick={() => {
              setSearch("");
              setProgrammeFilter("all");

              const nursingCourse =
                courseOptions.find((course) =>
                  normalize(course).includes(
                    "nursing"
                  )
                );

              if (nursingCourse) {
                setCourseFilter(
                  nursingCourse
                );
              } else {
                setCourseFilter("all");
                setProgrammeFilter(
                  "Nursing"
                );
              }

              setFiltersOpen(true);
            }}
          />

          <MetricCard
            icon={<GroupsOutlined />}
            value={midwiferyApplicants.length}
            label="Midwifery"
            description="Derived from loaded applicant records"
            accent="gold"
            active={
              programmeFilter ===
                "Midwifery" ||
              courseFilter === "Midwifery"
            }
            onClick={() => {
              setSearch("");
              setProgrammeFilter("all");

              const midwiferyCourse =
                courseOptions.find((course) =>
                  normalize(course).includes(
                    "midwifery"
                  )
                );

              if (midwiferyCourse) {
                setCourseFilter(
                  midwiferyCourse
                );
              } else {
                setCourseFilter("all");
                setProgrammeFilter(
                  "Midwifery"
                );
              }

              setFiltersOpen(true);
            }}
          />

          <MetricCard
            icon={<TuneOutlined />}
            value={filteredApplicants.length}
            label="Filtered view"
            description="Records matching current controls"
            accent="dark"
            active={activeFilterCount > 0}
            onClick={() =>
              setFiltersOpen(true)
            }
          />
        </section>

        {/* QUICK PROGRAMME INTELLIGENCE */}
        <section className="programme-intelligence">
          <div className="programme-intelligence__heading">
            <div>
              <span>QUICK SEGMENTS</span>
              <h2>Programme intelligence</h2>
            </div>

            <button
              type="button"
              onClick={() =>
                setFiltersOpen(true)
              }
            >
              <TuneOutlined />
              Advanced filtering
            </button>
          </div>

          <div className="programme-segments">
            <button
              type="button"
              className={
                courseFilter &&
                courseFilter !== "all" &&
                normalize(courseFilter).includes(
                  "nursing"
                )
                  ? "selected"
                  : ""
              }
              onClick={() => {
                setSearch("");
                setProgrammeFilter("all");

                const nursingCourse =
                  courseOptions.find((course) =>
                    normalize(course).includes(
                      "nursing"
                    )
                  );

                setCourseFilter(
                  nursingCourse || "all"
                );
              }}
            >
              <div>
                <SchoolOutlined />
              </div>

              <span>Nursing</span>

              <strong>
                {nursingApplicants.length}
              </strong>

              <small>
                applicants
              </small>
            </button>

            <button
              type="button"
              className={
                courseFilter &&
                courseFilter !== "all" &&
                normalize(courseFilter).includes(
                  "midwifery"
                )
                  ? "selected"
                  : ""
              }
              onClick={() => {
                setSearch("");
                setProgrammeFilter("all");

                const midwiferyCourse =
                  courseOptions.find((course) =>
                    normalize(course).includes(
                      "midwifery"
                    )
                  );

                setCourseFilter(
                  midwiferyCourse || "all"
                );
              }}
            >
              <div>
                <GroupsOutlined />
              </div>

              <span>Midwifery</span>

              <strong>
                {midwiferyApplicants.length}
              </strong>

              <small>
                applicants
              </small>
            </button>

            <button
              type="button"
              className={
                programmeFilter === "all" &&
                courseFilter === "all"
                  ? "selected"
                  : ""
              }
              onClick={() => {
                setProgrammeFilter("all");
                setCourseFilter("all");
              }}
            >
              <div>
                <DashboardOutlined />
              </div>

              <span>All programmes</span>

              <strong>
                {applicants.length}
              </strong>

              <small>
                applicants
              </small>
            </button>
          </div>
        </section>

        {/* EXPLORER */}
        <section className="report-explorer">
          <div className="report-search">
            <SearchOutlined />

            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search name, application, email, phone, programme..."
            />

            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
              >
                <CloseOutlined />
              </button>
            )}
          </div>

          <button
            type="button"
            className={`filter-trigger ${
              filtersOpen ? "active" : ""
            }`}
            onClick={() =>
              setFiltersOpen(!filtersOpen)
            }
          >
            <FilterAltOutlined />

            Filters

            {activeFilterCount > 0 && (
              <span>{activeFilterCount}</span>
            )}

            <KeyboardArrowDownOutlined />
          </button>

          <div className="density-switcher">
            <button
              type="button"
              className={
                changeview === "table"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setChangeView("table")
              }
            >
              <TableRowsOutlined />
            </button>

            <button
              type="button"
              className={
                changeview === "grid"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setChangeView("grid")
              }
            >
              <AppsOutlined />
            </button>
          </div>
        </section>

        {/* FILTER PANEL */}
        {filtersOpen && (
          <section className="report-filter-panel">
            <div className="report-filter-panel__header">
              <div>
                <span>
                  FILTER WORKSPACE
                </span>

                <h3>
                  Refine applicant intelligence
                </h3>
              </div>

              <button
                type="button"
                onClick={() =>
                  setFiltersOpen(false)
                }
              >
                <CloseOutlined />
              </button>
            </div>

            <div className="filter-summary">
              <div>
                <span>Matching records</span>
                <strong>
                  {filteredApplicants.length}
                </strong>
              </div>

              <div>
                <span>Current dataset</span>
                <strong>
                  {datasetLabel}
                </strong>
              </div>
            </div>

            <div className="report-filter-grid">
              <label>
                <span>Programme</span>

                <select
                  value={programmeFilter}
                  onChange={(event) =>
                    setProgrammeFilter(
                      event.target.value
                    )
                  }
                >
                  <option value="all">
                    All programmes
                  </option>

                  {programmeOptions.map(
                    (programme) => (
                      <option
                        value={programme}
                        key={programme}
                      >
                        {programme}
                      </option>
                    )
                  )}
                </select>
              </label>

              <label>
                <span>Course</span>

                <select
                  value={courseFilter}
                  onChange={(event) =>
                    setCourseFilter(
                      event.target.value
                    )
                  }
                >
                  <option value="all">
                    All courses
                  </option>

                  {courseOptions.map(
                    (course) => (
                      <option
                        value={course}
                        key={course}
                      >
                        {course}
                      </option>
                    )
                  )}
                </select>
              </label>

              <label>
                <span>Gender</span>

                <select
                  value={genderFilter}
                  onChange={(event) =>
                    setGenderFilter(
                      event.target.value
                    )
                  }
                >
                  <option value="all">
                    All genders
                  </option>

                  <option value="male">
                    Male
                  </option>

                  <option value="female">
                    Female
                  </option>
                </select>
              </label>

              <label>
                <span>Payment</span>

                <select
                  value={paymentFilter}
                  onChange={(event) =>
                    setPaymentFilter(
                      event.target.value
                    )
                  }
                >
                  <option value="all">
                    All payment states
                  </option>

                  <option value="success">
                    Successful
                  </option>

                  <option value="pending">
                    Pending
                  </option>

                  <option value="failed">
                    Failed
                  </option>
                </select>
              </label>

              <label>
                <span>Admission</span>

                <select
                  value={admissionFilter}
                  onChange={(event) =>
                    setAdmissionFilter(
                      event.target.value
                    )
                  }
                >
                  <option value="all">
                    All statuses
                  </option>

                  <option value="admitted">
                    Admitted
                  </option>

                  <option value="recommended">
                    Recommended
                  </option>

                  <option value="pending">
                    Pending
                  </option>

                  <option value="rejected">
                    Rejected
                  </option>
                </select>
              </label>
            </div>

            <div className="report-filter-panel__footer">
              <span>
                {filteredApplicants.length} records
                match the current configuration.
              </span>

              <div>
                <button
                  type="button"
                  className="filter-reset-button"
                  onClick={resetFilters}
                >
                  Reset all
                </button>

                <button
                  type="button"
                  className="filter-apply-button"
                  onClick={() =>
                    setFiltersOpen(false)
                  }
                >
                  Apply filters
                  <ArrowForwardOutlined />
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ACTIVE FILTERS */}
        {hasAnyFilter && (
          <section className="active-filter-bar">
            <div className="active-filter-bar__label">
              <TuneOutlined />
              Active controls
            </div>

            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
              >
                Search: {search}
                <CloseOutlined />
              </button>
            )}

            {programmeFilter !==
              "all" && (
              <button
                type="button"
                onClick={() =>
                  setProgrammeFilter("all")
                }
              >
                Programme:{" "}
                {programmeFilter}
                <CloseOutlined />
              </button>
            )}

            {courseFilter !== "all" && (
              <button
                type="button"
                onClick={() =>
                  setCourseFilter("all")
                }
              >
                Course: {courseFilter}
                <CloseOutlined />
              </button>
            )}

            {genderFilter !== "all" && (
              <button
                type="button"
                onClick={() =>
                  setGenderFilter("all")
                }
              >
                Gender: {genderFilter}
                <CloseOutlined />
              </button>
            )}

            {paymentFilter !== "all" && (
              <button
                type="button"
                onClick={() =>
                  setPaymentFilter("all")
                }
              >
                Payment: {paymentFilter}
                <CloseOutlined />
              </button>
            )}

            {admissionFilter !==
              "all" && (
              <button
                type="button"
                onClick={() =>
                  setAdmissionFilter("all")
                }
              >
                Admission:{" "}
                {admissionFilter}
                <CloseOutlined />
              </button>
            )}

            <button
              type="button"
              className="clear-all-filters"
              onClick={resetFilters}
            >
              Clear all
            </button>
          </section>
        )}

        {/* RESULTS HEADER */}
        <section className="report-results-header">
          <div>
            <span>LIVE RESULT SET</span>

            <h2>
              {filteredApplicants.length}

              <small>
                {filteredApplicants.length ===
                1
                  ? " applicant"
                  : " applicants"}
              </small>
            </h2>
          </div>

          <div className="report-results-actions">
            <div>
              <span>Dataset</span>
              <strong>{datasetLabel}</strong>
            </div>

            <div>
              <span>View</span>
              <strong>
                {changeview === "table"
                  ? "Table"
                  : "Cards"}
              </strong>
            </div>
          </div>
        </section>

        {/* ERROR */}
        {error && (
          <section className="report-error">
            <div>
              <strong>
                Report unavailable
              </strong>

              <span>{error}</span>
            </div>

            <button
              type="button"
              onClick={() =>
                fetchReport()
              }
            >
              Try again
            </button>
          </section>
        )}

        {/* EMPTY */}
        {!error &&
          filteredApplicants.length ===
            0 &&
          !loading && (
            <EmptyState
              clearFilters={resetFilters}
            />
          )}

        {/* DATA */}
        {!error &&
          filteredApplicants.length >
            0 &&
          (changeview === "table" ? (
            <TableView
              applicants={filteredApplicants}
              onView={setSelectedApplicant}
            />
          ) : (
            <CardView
              applicants={filteredApplicants}
              onView={setSelectedApplicant}
            />
          ))}

        {/* LOADING */}
        {loading && (
          <div className="report-loading">
            <div className="report-loading__panel">
              <div className="report-loading__icon">
                <RefreshOutlined />
              </div>

              <strong>
                Synchronizing intelligence
              </strong>

              <span>
                Fetching the latest applicant
                dataset...
              </span>
            </div>
          </div>
        )}

        {/* MODAL */}
        {selectedApplicant && (
          <ApplicantModal
            candidate={selectedApplicant}
            onClose={() =>
              setSelectedApplicant(null)
            }
          />
        )}
      </div>
    </main>
  );
}